import React, { useEffect, useState, useMemo } from "react";
import {
	collection,
	getDocs,
	updateDoc,
	deleteDoc,
	doc,
	query,
	orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	CartesianGrid,
} from "recharts";
import {
	LayoutDashboard,
	Users,
	Sun,
	Moon,
	LogOut,
	Upload,
} from "lucide-react";

/* ================= NETWORK DETECTION ================= */
const getNetworkFromPhone = (phone = "") => {
	const p = String(phone).replace(/\s+/g, "");

	if (
		p.startsWith("024") ||
		p.startsWith("054") ||
		p.startsWith("055") ||
		p.startsWith("059")
	) {
		return "MTN";
	}

	if (p.startsWith("020") || p.startsWith("050")) {
		return "Telecel";
	}

	if (p.startsWith("027") || p.startsWith("057") || p.startsWith("026")) {
		return "AirtelTigo";
	}

	return "Unknown";
};

/* ================= STATUS BADGE ================= */
const StatusBadge = ({ status }) => {
	if (status === "completed") {
		return (
			<span className="inline-flex items-center gap-1 text-green-500 font-semibold">
				<span className="w-3 h-3 rounded-full border border-green-500 flex items-center justify-center text-[10px]">
					✓
				</span>
				Done
			</span>
		);
	}

	if (status === "processing") {
		return (
			<span className="inline-flex items-center gap-1 text-blue-500 font-semibold">
				<span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
				Processing
			</span>
		);
	}

	return (
		<span className="inline-flex items-center gap-1 text-yellow-500 font-semibold">
			<span className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
			Pending
		</span>
	);
};

/* ================= MOBILE NAV ================= */
const MobileNav = ({ icon, label, link, onClick }) => {
	const Tag = link ? "a" : "button";
	return (
		<Tag
			href={link}
			onClick={onClick}
			className="flex flex-col items-center text-xs font-medium"
		>
			{icon}
			<span>{label}</span>
		</Tag>
	);
};

/* ================= COUNTER ================= */
const Counter = ({ title, value, isDark }) => (
	<div
		className={`p-4 rounded-xl shadow text-center ${
			isDark ? "bg-slate-800" : "bg-white"
		}`}
	>
		<p className="text-sm opacity-70">{title}</p>
		<p className="text-2xl font-extrabold">{value}</p>
	</div>
);

/* ================= MAIN COMPONENT ================= */
export default function Transactions() {
	const [transactions, setTransactions] = useState([]);
	const [phoneFilter, setPhoneFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [networkFilter, setNetworkFilter] = useState("all");
	const [selectedIds, setSelectedIds] = useState([]);
	const [loading, setLoading] = useState(true);

	/* THEME */
	const [theme, setTheme] = useState(
		localStorage.getItem("admin-theme") || "light"
	);
	const isDark = theme === "dark";

	useEffect(() => {
		localStorage.setItem("admin-theme", theme);
		document.documentElement.classList.toggle("dark", isDark);
	}, [theme, isDark]);

	/* PAGINATION */
	const ITEMS_PER_PAGE = 10;
	const [currentPage, setCurrentPage] = useState(1);

	/* LOAD TRANSACTIONS */
	const loadTransactions = async () => {
		setLoading(true);
		const snap = await getDocs(
			query(collection(db, "credentials"), orderBy("assignedAt", "desc"))
		);

		setTransactions(
			snap.docs.map((d) => ({
				docId: d.id,
				...d.data(),
			}))
		);
		setLoading(false);
	};

	useEffect(() => {
		loadTransactions();
	}, []);

	/* FILTER */
	const filteredTx = useMemo(() => {
		return transactions.filter((t) => {
			const phoneMatch = phoneFilter ? t.assignedTo === phoneFilter : true;
			const statusMatch =
				statusFilter === "all" ? true : t.status === statusFilter;

			const network = t.network || getNetworkFromPhone(t.assignedTo);
			const networkMatch = networkFilter === "all" ? true : network === networkFilter;

			return phoneMatch && statusMatch && networkMatch;
		});
	}, [transactions, phoneFilter, statusFilter, networkFilter]);

	useEffect(() => {
		setCurrentPage(1);
		setSelectedIds([]);
	}, [phoneFilter, statusFilter, networkFilter]);

	/* PAGINATED DATA */
	const totalPages = Math.max(1, Math.ceil(filteredTx.length / ITEMS_PER_PAGE));

	const paginatedTx = useMemo(() => {
		const start = (currentPage - 1) * ITEMS_PER_PAGE;
		return filteredTx.slice(start, start + ITEMS_PER_PAGE);
	}, [filteredTx, currentPage]);

	/* COUNTERS */
	const statusCounters = useMemo(() => {
		const c = { pending: 0, processing: 0, completed: 0 };
		filteredTx.forEach((t) => {
			if (c[t.status] !== undefined) c[t.status] += 1;
		});
		return c;
	}, [filteredTx]);

	/* CHART */
	const statusRevenueData = useMemo(() => {
		const r = { pending: 0, processing: 0, completed: 0 };
		filteredTx.forEach((t) => {
			if (r[t.status] !== undefined) r[t.status] += Number(t.totalPaid || 0);
		});

		return [
			{ status: "Pending", revenue: r.pending },
			{ status: "Processing", revenue: r.processing },
			{ status: "Completed", revenue: r.completed },
		];
	}, [filteredTx]);

	/* EXPORT CSV */
	const exportCSV = () => {
		if (!filteredTx.length) {
			alert("No transactions to export");
			return;
		}

		const headers = [
			"Date",
			"Time",
			"Phone",
			"Package",
			"Amount (GHS)",
			"Status",
		];

		const rows = filteredTx.map((t) => {
			const dt = t.assignedAt?.toDate?.() || null;
			return [
				dt ? dt.toLocaleDateString() : "",
				dt ? dt.toLocaleTimeString() : "",
				t.assignedTo || "",
				t.packageId || "",
				Number(t.totalPaid || 0).toFixed(2),
				t.status || "",
			];
		});

		const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "transactions.csv");
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	/* SINGLE ACTIONS */
	const updateStatus = async (tx, status) => {
		await updateDoc(doc(db, "credentials", tx.docId), { status });
		loadTransactions();
	};

	const deleteTransaction = async (tx) => {
		if (!window.confirm("Delete transaction?")) return;
		await deleteDoc(doc(db, "credentials", tx.docId));
		loadTransactions();
	};

	/* BULK ACTIONS (REQUESTED) */
	const updateAllByStatus = async (fromStatus, toStatus, label) => {
		const matches = filteredTx.filter((t) => t.status === fromStatus);

		if (!matches.length) {
			alert(`No ${fromStatus} transactions`);
			return;
		}

		if (!window.confirm(`${label}: ${matches.length} transaction(s)?`)) return;

		await Promise.all(
			matches.map((t) =>
				updateDoc(doc(db, "credentials", t.docId), { status: toStatus })
			)
		);

		setSelectedIds([]);
		loadTransactions();
	};

	const allPendingToProcessing = async () => {
		await updateAllByStatus(
			"pending",
			"processing",
			"Move all pending to processing"
		);
	};

	const allProcessingToCompleted = async () => {
		await updateAllByStatus(
			"processing",
			"completed",
			"Move all processing to completed"
		);
	};

	const selectedToCompleted = async () => {
		if (!selectedIds.length) {
			alert("No transactions selected");
			return;
		}

		if (!window.confirm(`Update ${selectedIds.length} selected to completed?`)) {
			return;
		}

		await Promise.all(
			selectedIds.map((id) =>
				updateDoc(doc(db, "credentials", id), { status: "completed" })
			)
		);

		setSelectedIds([]);
		loadTransactions();
	};

	const handleLogout = () => {
		localStorage.clear();
		window.location.href = "/agent/login";
	};

	if (loading) {
		return <p className="p-6 text-white">Loading transactions...</p>;
	}

	return (
		<div
			className={`min-h-screen p-4 md:p-6 pb-24 ${
				isDark ? "bg-slate-900 text-white" : "bg-slate-100 text-gray-900"
			}`}
		>
			<div className="max-w-7xl mx-auto space-y-6">
				<div className="hidden md:flex justify-between items-center">
					<h1 className="text-2xl font-extrabold">Transactions</h1>

					<div className="flex gap-3">
						<button
							onClick={() => {
								window.location.href = "/admin/dashboard";
							}}
							className="px-5 py-2 bg-sky-600 text-white rounded-lg font-semibold"
						>
							⬅ Dashboard
						</button>

						<button
							onClick={exportCSV}
							className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold"
						>
							Export CSV
						</button>

						<button
							onClick={() => setTheme(isDark ? "light" : "dark")}
							className={`px-4 py-2 rounded-lg border font-semibold ${
								isDark
									? "bg-slate-800 text-yellow-400 border-slate-600"
									: "bg-white text-gray-800 border-gray-300"
							}`}
						>
							{isDark ? "☀ Light" : "🌙 Dark"}
						</button>
					</div>
				</div>

				<div className="flex gap-3 flex-col md:flex-row">
					<input
						placeholder="Filter by phone"
						value={phoneFilter}
						onChange={(e) => setPhoneFilter(e.target.value)}
						className={`p-3 rounded border w-full md:w-1/3 ${
							isDark ? "bg-black text-white border-slate-700" : "bg-white"
						}`}
					/>

					<select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						className={`p-3 rounded border w-full md:w-1/4 ${
							isDark ? "bg-black text-white border-slate-700" : "bg-white"
						}`}
					>
						<option value="all">All Status</option>
						<option value="pending">Pending</option>
						<option value="processing">Processing</option>
						<option value="completed">Completed</option>
					</select>

					<select
						value={networkFilter}
						onChange={(e) => setNetworkFilter(e.target.value)}
						className={`p-3 rounded border w-full md:w-1/4 ${
							isDark ? "bg-black text-white border-slate-700" : "bg-white"
						}`}
					>
						<option value="all">All Networks</option>
						<option value="MTN">MTN</option>
						<option value="AirtelTigo">AirtelTigo</option>
						<option value="Telecel">Telecel</option>
					</select>
				</div>

				<div className="grid grid-cols-3 gap-3">
					<Counter title="Pending" value={statusCounters.pending} isDark={isDark} />
					<Counter title="Processing" value={statusCounters.processing} isDark={isDark} />
					<Counter title="Completed" value={statusCounters.completed} isDark={isDark} />
				</div>

				<div className="flex flex-wrap gap-3">
					<button
						onClick={allPendingToProcessing}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
					>
						All Pending → Processing
					</button>

					<button
						onClick={allProcessingToCompleted}
						className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
					>
						All Processing → Completed
					</button>

					<button
						onClick={selectedToCompleted}
						className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm"
					>
						Selected → Completed
					</button>
				</div>

				<div
					className={`rounded-xl shadow overflow-x-auto ${
						isDark ? "bg-slate-800" : "bg-white"
					}`}
				>
					<table className="w-full text-sm">
						<thead className={isDark ? "bg-slate-700" : "bg-gray-100"}>
							<tr>
								<th className="p-3">
									<input
										type="checkbox"
										onChange={(e) =>
											setSelectedIds(
												e.target.checked ? paginatedTx.map((t) => t.docId) : []
											)
										}
										checked={
											paginatedTx.length > 0 &&
											paginatedTx.every((t) => selectedIds.includes(t.docId))
										}
									/>
								</th>
								<th className="p-3 text-left">Amount</th>
								<th className="p-3 text-left">Phone</th>
								<th className="p-3 text-left">Status</th>
								<th className="p-3 text-left">Date</th>
								<th className="p-3 text-left">Actions</th>
							</tr>
						</thead>

						<tbody>
							{paginatedTx.map((t) => (
								<tr key={t.docId} className="border-b">
									<td className="p-3">
										<input
											type="checkbox"
											checked={selectedIds.includes(t.docId)}
											onChange={(e) => {
												setSelectedIds((prev) =>
													e.target.checked
														? [...prev, t.docId]
														: prev.filter((id) => id !== t.docId)
												);
											}}
										/>
									</td>

									<td className="p-3 font-bold">
										GHS {Number(t.totalPaid || 0).toFixed(2)}
										<div className="text-xs opacity-70">{t.packageId}</div>
									</td>

									<td className="p-3">{t.assignedTo}</td>

									<td className="p-3">
										<StatusBadge status={t.status} />
									</td>

									<td className="p-3">{t.assignedAt?.toDate?.().toLocaleDateString?.()}</td>

									<td className="p-3 flex gap-2">
										<select
											value={t.status}
											onChange={(e) => updateStatus(t, e.target.value)}
											className={`text-xs px-2 py-1 rounded ${
												isDark ? "bg-black text-white" : "bg-white"
											}`}
										>
											<option value="pending">Pending</option>
											<option value="processing">Processing</option>
											<option value="completed">Completed</option>
										</select>

										<button
											onClick={() => deleteTransaction(t)}
											className="px-2 py-1 bg-red-600 text-white text-xs rounded"
										>
											Delete
										</button>
									</td>
								</tr>
							))}

							{!paginatedTx.length && (
								<tr>
									<td colSpan={6} className="p-4 text-center opacity-70">
										No transactions found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className="flex justify-center items-center gap-4">
					<button
						disabled={currentPage === 1}
						onClick={() => setCurrentPage((p) => p - 1)}
						className="px-4 py-2 bg-gray-700 text-white rounded disabled:opacity-40"
					>
						◀ Prev
					</button>

					<span className="font-semibold">
						Page {currentPage} of {totalPages}
					</span>

					<button
						disabled={currentPage === totalPages}
						onClick={() => setCurrentPage((p) => p + 1)}
						className="px-4 py-2 bg-gray-700 text-white rounded disabled:opacity-40"
					>
						Next ▶
					</button>
				</div>

				<div className={`p-6 rounded-xl shadow ${isDark ? "bg-slate-800" : "bg-white"}`}>
					<p className="font-bold mb-4">Revenue by Status</p>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={statusRevenueData}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="status" />
							<YAxis />
							<Tooltip />
							<Bar dataKey="revenue" fill="#22c55e" />
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>

			<div
				className={`fixed bottom-0 left-0 right-0 md:hidden border-t flex justify-around py-3 ${
					isDark ? "bg-black/90 border-white/10" : "bg-white border-gray-200"
				}`}
			>
				<button onClick={exportCSV} className="flex flex-col items-center text-green-500">
					<Upload size={22} />
					<span className="text-xs">Export</span>
				</button>

				<MobileNav
					icon={<LayoutDashboard size={22} />}
					label="Dashboard"
					link="/admin/dashboard"
				/>
				<MobileNav icon={<Users size={22} />} label="Users" link="/admin/users" />

				<button
					onClick={() => setTheme(isDark ? "light" : "dark")}
					className="flex flex-col items-center text-yellow-500"
				>
					{isDark ? <Sun size={22} /> : <Moon size={22} />}
					<span className="text-xs">Theme</span>
				</button>

				<button onClick={handleLogout} className="flex flex-col items-center text-red-500">
					<LogOut size={22} />
					<span className="text-xs">Logout</span>
				</button>
			</div>
		</div>
	);
}
